<template>
  <div class="distance-selector" ref="dropdownRef">
    <span class="slider-label">{{ label }}</span>
    <!-- Dropdown trigger -->
    <div class="dropdown-trigger" :class="{ 'is-active': isOpen }" @click="toggleDropdown">
      <span v-if="value">{{ value }} {{ unit }}</span>
      <span class="placeholder" v-else>{{ placeholder }}</span>
      <span class="caret bp-icon-Chevron"></span>
    </div>

    <!-- Dropdown menu -->
    <div v-if="isOpen" class="dropdown-menu">
      <!-- Slider -->
      <div class="slider">
        <input id="slider" type="range" v-model.number="value" :min="minValue" :max="maxValue" />
      </div>

      <!-- Numeric input -->
      <div class="input-group">
        <input id="numericInput" type="number" v-model.number="value" :min="minValue" :max="maxValue" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, defineEmits, defineProps, watch } from 'vue';

const emit = defineEmits(['updateSlider', 'update:modelValue']);

const props = defineProps({
  minValue: {
    type: Number,
    default: 0,
  },
  maxValue: {
    type: Number,
    default: 100,
  },
  unit: {
    type: String,
    default: 'km',
  },
  label: {
    type: String,
    default: 'Label',
  },
  initialValue: {
    type: Number,
    default: 0,
  },
  placeholder: {
    type: String,
    default: '',
  },
  id: {
    type: String,
  },
  modelValue: Number
});
const value = ref<number>(props.modelValue ?? props.initialValue);
const isOpen = ref(false);
const dropdownRef = ref<HTMLElement | null>(null);

// Sincronizar `modelValue` con `value` cuando cambie desde el padre
watch(() => props.modelValue, (newValue) => {
  if (newValue !== undefined) {
    value.value = Math.min(Math.max(newValue, props.minValue), props.maxValue); // Validación
  }
});

// Emitir cambios cuando el usuario mueva el slider o el input
watch(value, (newValue) => {
  const validatedValue = Math.min(Math.max(newValue, props.minValue), props.maxValue);
  if (validatedValue !== newValue) {
    value.value = validatedValue;
  }
  emit("update:modelValue", validatedValue);
  emit("updateSlider", props.id, validatedValue);
});

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const handleClickOutside = (event: MouseEvent) => {
  if (isOpen.value && dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style lang="scss" scoped>
@import 'SliderSelectorComponent';
</style>